import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import store from 'store'
import { useNavigate, useLocation } from 'react-router-dom'
import { UserTypeEnum } from '../../utils/enums'
import { getMissingStudentFields } from '../../utils/studentValidation'
import Modal from '../modal'
import useI18n from '../../i18n/useI18n'

const StudentProfileReminder = () => {
  const { t } = useI18n()
  const userInfo = useSelector((state) => state.auth?.userInfo || {})
  const [missingFields, setMissingFields] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const COOLDOWN_MS = 60 * 60 * 1000

  const resolveStudentData = (source) => {
    if (!source) return null
    return (
      source.student ||
      source.studentInfo ||
      source.studentProfile ||
      store.get('studentProfile') ||
      null
    )
  }

  useEffect(() => {
    if (userInfo?.userType !== UserTypeEnum.STUDENT) {
      setMissingFields([])
      setIsLoading(false)
      return
    }

    const studentData = resolveStudentData(userInfo)
    if (studentData) {
      setMissingFields(getMissingStudentFields(studentData))
      setIsLoading(false)
    } else {
      // No data available; avoid fetching per requirement
      setMissingFields([])
      setIsLoading(false)
    }
  }, [userInfo])

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedStudent = event?.detail || resolveStudentData(userInfo) || store.get('studentProfile')
      if (updatedStudent) {
        setMissingFields(getMissingStudentFields(updatedStudent))
      }
    }

    window.addEventListener('studentProfileUpdated', handleProfileUpdate)
    return () => {
      window.removeEventListener('studentProfileUpdated', handleProfileUpdate)
    }
  }, [userInfo])

  useEffect(() => {
    if (userInfo?.userType !== UserTypeEnum.STUDENT) {
      setShowModal(false)
      return
    }

    const isOnProfilePage = location.pathname.startsWith('/profile')
    const now = Date.now()
    const lastSeen = store.get('studentProfileReminderSeenAt')
    const cooldownActive = lastSeen && now - lastSeen < COOLDOWN_MS

    if (!isLoading && missingFields.length > 0 && !isOnProfilePage && !cooldownActive) {
      setShowModal(true)
      store.set('studentProfileReminderSeenAt', now)
    } else {
      setShowModal(false)
    }
  }, [missingFields, userInfo?.userType, isLoading, location.pathname])

  const handleClose = () => {
    setShowModal(false)
  }

  if (userInfo?.userType !== UserTypeEnum.STUDENT || isLoading || missingFields.length === 0) {
    return null
  }

  const handleCompleteProfile = () => {
    setShowModal(false)
    navigate('/profile?tab=student')
  }

  return (
    <Modal
      isOpen={showModal}
      onClose={handleClose}
      title={t('STUDENT_PROFILE_REMINDER_TITLE')}
      subtitle={t('STUDENT_PROFILE_REMINDER_SUBTITLE')}
      icon="zmdi-alert-triangle"
      size="medium"
    >
      <p className="mb-2">
        {t('STUDENT_PROFILE_REMINDER_DESCRIPTION')}
      </p>
      <ul className="mb-3">
        {missingFields.map((field) => (
          <li key={field} style={{ fontWeight: 500 }}>
            {t(field)}
          </li>
        ))}
      </ul>
      <div className="text-right">
        <button
          type="button"
          className="btn btn-default btn-round m-r-10"
          onClick={handleClose}
        >
          {t('COMMON_ACTIONS_CLOSE')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-round"
          onClick={handleCompleteProfile}
        >
          {t('STUDENT_PROFILE_REMINDER_BUTTON')}
        </button>
      </div>
    </Modal>
  )
}

export default StudentProfileReminder

